import { useState, useEffect } from 'react'
import Avatar from '@wiggui/ds/Avatar'
import IconWiggot from '@wiggui/ds/IconWiggot'
import Spinner from '@wiggui/ds/Spinnner'
import { MdOutlineLogout } from 'react-icons/md'
import { useSession } from '../../../../auth/hooks/useSession'
import { createUrlMedia } from '../../../../utils/src/media-image'
import { composeClasses } from '../../../../utils/src/strings'
import { useGetMenuOptions } from '../hooks/useGetMenuOptions'
import { useAuth } from '../../../hooks/useAuth'
import { ROUTES } from '../../../constants/routes'
import { SidebarOption } from './SidebarOption'
import { IntercomButton } from '../../../components/IntercomButton'
import HardLink from '../../../components/hard-link/HardLink'

interface SidebarMenuProps {
  isVisible: boolean | null
  setShowSideMenuOptions: (value: boolean) => void
}

export const SidebarMobile = ({
  isVisible,
  setShowSideMenuOptions
}: SidebarMenuProps) => {
  const { isLoadingLogout, handleLogout } = useAuth()
  const { profile } = useSession()
  const { menuOptions } = useGetMenuOptions()

  const findOptionActive = menuOptions?.find((option) => option?.isActive)?.id

  const [expandedOptions, setExpandedOptions] = useState<string>(
    findOptionActive ?? ''
  )

  const avatarUrl = profile?.photo
    ? createUrlMedia({ id: profile?.photo, size: 's' })
    : undefined

  useEffect(() => {
    if (findOptionActive) {
      setExpandedOptions(findOptionActive)
    }
  }, [findOptionActive])

  const getEffectSideBar = () => {
    if (isVisible === null) {
      return 'hidden'
    }
    return isVisible ? 'sidebar-enter-active' : 'sidebar-enter'
  }

  return (
    <div
      className={composeClasses(
        'w-full h-full max-w-[100dvw] max-h-[calc(100dvh-65px)] bg-white fixed z-3 top-0 left-0 right-0 md:hidden',
        getEffectSideBar()
      )}
    >
      <div className="flex items-center justify-between px-6 py-3 gap-2.5 border-b border-surface-strokes w-full">
        <div className="flex items-center gap-2.5 justify-start">
          <IconWiggot variant="black" width="24px" height="24px" />
          <span className="text-body-bold-xl text-primary">Menú</span>
        </div>
        <div className="flex items-center gap-2.5 justify-end flex-row">
          <IntercomButton
            containerTextClassName="hidden"
            showText={false}
            className="min-w-[48px] min-h-[48px] max-h-[48px] max-w-[48px] flex items-center justify-center"
            iconClassName="min-w-[24px] min-h-[24px] max-h-[24px] max-w-[24px]"
          />
          <HardLink
            href={ROUTES.ACCOUNT_PROFILE}
            className="active:brightness-75"
            onClick={() => {
              setShowSideMenuOptions(false)
            }}
          >
            <Avatar
              containerClassName="min-w-[48px] min-h-[48px] max-h-[48px] max-w-[48px] [&>img]:!rounded-xl [&>img]:border-2 [&>img]:border-surface-strokes"
              avatarUrl={avatarUrl}
              fullName={profile?.fullname}
            />
          </HardLink>
        </div>
      </div>
      <div className="w-full h-full flex flex-col justify-between max-h-[calc(100dvh-150px)] ui-kit-scrollbar overflow-y-auto">
        <ul className="w-full">
          {menuOptions?.map((option) => (
            <SidebarOption
              setShowSideMenuOptions={setShowSideMenuOptions}
              expandedOption={expandedOptions}
              setExpandedOption={setExpandedOptions}
              option={option}
              key={option?.id}
            />
          ))}
        </ul>
        <div className="w-full">
          <button
            type="button"
            onClick={() => handleLogout(undefined)}
            className={composeClasses(
              'flex mx-3 px-3 py-2 items-center gap-2 text-destructive active:bg-surface-gray-10 rounded-md',
              isLoadingLogout && 'pointer-events-none'
            )}
          >
            {isLoadingLogout ? (
              <Spinner customBorderColor="border-t-destructive" />
            ) : (
              <MdOutlineLogout className="min-w-[18px] min-h-[18px]" />
            )}
            <span className="text-body-bold-m">
              {isLoadingLogout ? 'Cerrando sesión...' : 'Cerrar sesión'}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}
